import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { CardItem, HomePageEventsName, ProductItem } from '../types';
import { AvailableProductTrialType } from '../../types/AvailableProductTrialType';
import { FontSizes, FontWeights } from '../../../../types/common/types';
import { ImgPosition } from '../../../common/gzTrialProductItem/type';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../ui/gzButton/types';
import { translateFn } from '../../../../utils/translator.helper';
import { purchaseCardEntitySet } from '../../../../company/myCompany/purchase/purchasePage/uiSetIcons';
import { GzCardWebComponent } from '../../../../ui/gzCard';
import { PropertyValues } from '@lit/reactive-element';

@customElement('gz-trial-home-page')
export class GzTrialHomePageWebComponent extends LitElement {
  @property({
    type: Array,
    attribute: false,
    reflect: false,
  })
  cardItems: Array<CardItem> = [];

  @property({
    type: Array,
    attribute: false,
    reflect: false,
  })
  productItems: Array<ProductItem> = [];

  @property({ type: Object })
  texts = {
    cardItemsTitle: '',
    titleEmptyStateText: '',
    descriptionEmptyStateText: '',
  };

  @query('gz-card')
  cardElement: GzCardWebComponent;

  static get styles() {
    return styles;
  }

  updated(_changedProperties: PropertyValues): void {
    super.updated(_changedProperties);
    if (this.cardElement) {
      this.cardElement.setCustomIconSet(purchaseCardEntitySet);
    }
  }

  render() {
    return html`
      <div class="wrapper d-flex flex-column">
        <div class="products-section d-flex flex-row">${this.renderProducts()}</div>
        <div class="card-section">
          ${this.renderCardItemsTitle()}
          <div class="card-section-container layout-3-columns">${this.renderCards()}</div>
          ${this.renderEmptyStateCards()}
        </div>
      </div>
    `;
  }

  private renderCardItemsTitle(): TemplateResult | undefined {
    if (!this.cardItems.length || !this.productItems.length) {
      return;
    }

    return html`<gz-title class="card-section-title pl-3 pb-3">${this.texts.cardItemsTitle}</gz-title>`;
  }

  private renderEmptyStateCards(): TemplateResult | undefined {
    if (this.cardItems.length) {
      return;
    }
    return html`<gz-image-repo state="start"
      ><gz-title class="empty-title" fontSize="${FontSizes.HEADING_4}" fontWeight="${FontWeights.MEDIUM}"
        >${this.texts.titleEmptyStateText}</gz-title
      ><gz-text class="empty-description" fontSize="${FontSizes.PARAGRAPH_12}"
        >${this.texts.descriptionEmptyStateText}</gz-text
      ></gz-image-repo
    >`;
  }

  private renderCards(): TemplateResult[] | undefined {
    if (!this.cardItems.length) {
      return;
    }
    return this.cardItems.map(
      item =>
        html`
          <gz-card
            cardwidth="500"
            class="cursor"
            icon="${item.icon}"
            name="${item.cardName}"
            tagInformation="${item.tagInformation}"
            tagAdditionalInformation="${item.canBuy ? translateFn('Buy now') : ''}"
            @click="${() => this.handleProductClicked(item.type)}"
          >
            <div class="pb-2">${item.text}</div>

            <gz-button
              class="pt-2"
              style="bottom: calc(var(--spacer) * 2); position: absolute;"
              type="${ButtonTypes.QUIET}"
              color="${ButtonColors.PRIMARY}"
              buttonHeight="${ButtonHeights.SMALL}"
              buttonFontSize="${ButtonFontSizes.SMALL}"
              id=${item.buttonId}
            >
              ${item.buttonText}
            </gz-button>
          </gz-card>
        `
    );
  }

  private renderProducts(): TemplateResult[] | undefined {
    if (!this.productItems.length) {
      return;
    }

    // get image alignment
    const imageAlignment = this.productItems.length === 1 ? ImgPosition.LEFT : ImgPosition.TOP;

    return this.productItems.map(
      item => html`
        <gz-trial-product-item
          productTitle="${item.title}"
          description="${item.description}"
          imageAlignment="${imageAlignment}"
          imageSrc="${item.imageSrc}"
          ><gz-button id=${item.buttonId} @click="${() => this.handleProductClicked(item.type)}"
            >${item.buttonText}</gz-button
          >
        </gz-trial-product-item>
      `
    );
  }

  private handleProductClicked(type: AvailableProductTrialType): void {
    const clickEvent = new CustomEvent(HomePageEventsName.PRODUCT_ITEM, {
      detail: {
        type: type,
      },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(clickEvent);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    gzTrialHomePage: GzTrialHomePageWebComponent;
  }
}
